import { useCallback } from 'react';
import { useConversationStore } from '@/stores/conversation-store';
import { useAuthStore } from '@/stores/auth-store';
import { usePermissions } from './use-permissions';

/**
 * Hook for conversation/inbox management.
 *
 * Conversations are fetched when a sender is selected via setActiveSender.
 */
export function useConversations() {
  const store = useConversationStore();
  const { inboxPermissions, isAdmin } = usePermissions();

  const refresh = useCallback(() => {
    store.fetchConversations();
  }, [store]);

  const currentUserEmail = useAuthStore((s) => s.user?.email);

  const filteredConversations = store.conversations.filter((c) => {
    // Channel filter: when a specific sender is selected, only show conversations
    // matching that sender's channel (e.g. SMS sender should not show WhatsApp convos)
    if (store.activeSender?.channel && c.channel && c.channel !== store.activeSender.channel) {
      return false;
    }

    // Inbox permission: hide unassigned conversations when not allowed.
    // The "All" tab fetches everything — this client-side filter excludes
    // unassigned conversations when the permission is off.
    if (!isAdmin && !inboxPermissions.canViewUnassignedChats) {
      const assignee = c.assigneeEmail;
      if (!assignee || assignee === 'unassigned') return false;
    }

    // Quick tab / agent filter (client-side fallback for 'unassigned' and a
    // specific agent email; 'mine' is server-side)
    if (store.filterAgent && store.filterAgent !== 'all') {
      const assignee = c.assigneeEmail || 'unassigned';
      if (store.filterAgent === 'unassigned') {
        if (assignee !== 'unassigned') return false;
      } else if (store.filterAgent !== 'mine' && !store.phoneSearchActive) {
        // Specific agent email. WhatsApp/Twilio already filter this in the
        // Firestore query; REST TPAs (MessageBird, RingCentral, Intercom, …)
        // ignore `assigneeEmail`, so narrow here too. Skip during phone search:
        // searchByPhone never applies an assignee filter (same as the text-search
        // guard below), so the searched contact's chat must stay visible.
        if (assignee !== store.filterAgent) return false;
      }
    }

    // Label filter (client-side for __unread__ and unreplied; others are server-side)
    if (store.filterLabel) {
      if (store.filterLabel === 'unreplied') {
        const lastDir = c.metadata?.lastMessageDirection as string | undefined;
        if (lastDir !== 'inbound') return false;
      } else if (store.filterLabel === '__unread__') {
        if (!c.unreadCount || c.unreadCount <= 0) return false;
      }
      // Custom labels, __send_failed__ are now filtered server-side via Firestore where clause
    }

    // Text search — skip when phone search is active (server already filtered by phone)
    if (store.searchQuery && !store.phoneSearchActive) {
      const q = store.searchQuery.toLowerCase();
      return (
        (c.contactName || '').toLowerCase().includes(q) ||
        (c.contactPhone || '').includes(q) ||
        (c.lastMessage || '').toLowerCase().includes(q)
      );
    }
    return true;
  });

  return {
    conversations: filteredConversations,
    activeConversation: store.activeConversation,
    activeSender: store.activeSender,
    messages: store.messages,
    loading: store.loading,
    loadingMore: store.loadingMore,
    messagesLoading: store.messagesLoading,
    hasMore: store.hasMore,
    searchQuery: store.searchQuery,
    filterLabel: store.filterLabel,
    filterAgent: store.filterAgent,
    jumpBefore: store.jumpBefore,
    chatLabels: store.chatLabels,
    error: store.error,
    phoneSearchActive: store.phoneSearchActive,
    setActiveConversation: store.setActiveConversation,
    setActiveSender: store.setActiveSender,
    setSearchQuery: store.setSearchQuery,
    setFilterChannel: store.setFilterChannel,
    setFilterLabel: store.setFilterLabel,
    setFilterAgent: store.setFilterAgent,
    jumpToConversationsAtDate: store.jumpToConversationsAtDate,
    searchByPhone: store.searchByPhone,
    refresh,
    loadMore: store.loadMoreConversations,
    jumpToMessagesAtDate: store.jumpToMessagesAtDate,
    addMessage: store.addMessage,
    markAsRead: store.markAsRead,
  };
}
